import React from 'react';
import { Check, Copy } from 'lucide-react';
import { Interpretation } from '../types';
import { cn } from '../../../utils/cn';

/**
 * Wspólny renderer 6 sekcji interpretacji — używany przez ResultsDisplay
 * (panel, size="compact") i FullscreenResultDialog (size="reading").
 * Jedno źródło prawdy dla nagłówków/kolorów sekcji; bez duplikacji.
 *
 * BEZ edycji per sekcja (decyzja właściciela, PLAN-REFACTOR 4.2): diagnozę
 * edytuje się JAKO CAŁOŚĆ w edytorze markdown FullscreenResultDialog.
 */

type ListContent = string[] | string | undefined;

interface SectionDef {
  /** Tytuł używany przy kopiowaniu pojedynczej sekcji (istniejące zachowanie). */
  copyTitle: string;
  /** Nagłówek wyświetlany w UI (z emoji). */
  header: string;
  /** Nagłówek do czystego tekstu „Kopiuj całość" (bez emoji). */
  plainHeader: string;
  /** Klasa kaskadowej animacji wejścia (ai-section-N) — pomijana, gdy animateIn=false. */
  animClass: string;
  /** Klasy kolorystyczne kontenera sekcji. */
  boxClass: string;
  /** Klasy przycisku kopiowania. */
  copyBtnClass: string;
  /** Klasy nagłówka. */
  headerClass: string;
  /** Wyciąga treść sekcji z interpretacji. */
  getContent: (i: Interpretation) => ListContent;
  /** Czy treść jest listą zaleceń. */
  isList?: boolean;
}

// UWAGA — ŚWIADOMA DECYZJA (nie „naprawiać" przy kolejnych review):
// klasy blue/amber/purple/indigo/pink (z wariantami dark:) to TOŻSAMOŚCIOWE
// Tła sekcji: wariant „Mróz kliniczny” (decyzja Arka 2026-10-01) — wszystkie sekcje na
// neutralnym, jasnym tle tokenów (surface-subtle + border), „mocne strony” na accent-soft.
// Tożsamość sekcji niosą kolorowe nagłówki i przyciski kopiowania (headerClass/copyBtnClass) —
// te kolory zostają poza systemem tokenów; ich tokenizacja = osobna decyzja projektowa.
const SECTION_BOX = 'border-brd bg-surface-subtle';

const SECTIONS: SectionDef[] = [
  {
    copyTitle: 'Ogólna ocena',
    header: '📋 Ogólna ocena rozwoju',
    plainHeader: 'Ogólna ocena rozwoju',
    animClass: 'ai-section-1',
    boxClass: SECTION_BOX,
    copyBtnClass: 'hover:bg-blue-50 dark:hover:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
    headerClass: 'text-blue-800 dark:text-blue-300',
    getContent: (i) => i.general_assessment,
  },
  {
    copyTitle: 'Mocne strony',
    header: '💪 Funkcjonalne mocne strony',
    plainHeader: 'Funkcjonalne mocne strony',
    animClass: 'ai-section-2',
    boxClass: 'border-accent-border bg-accent-soft',
    copyBtnClass: 'hover:bg-accent-soft border-accent-border text-accent-strong',
    headerClass: 'text-accent-strong',
    getContent: (i) => i.strengths,
  },
  {
    copyTitle: 'Obszary trudności',
    header: '🎯 Obszary wymagające wsparcia',
    plainHeader: 'Obszary wymagające wsparcia',
    animClass: 'ai-section-3',
    boxClass: SECTION_BOX,
    copyBtnClass: 'hover:bg-amber-50 dark:hover:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300',
    headerClass: 'text-amber-800 dark:text-amber-300',
    getContent: (i) => i.areas_for_support || i.difficulties,
  },
  {
    copyTitle: 'Funkcjonowanie szkolne',
    header: '🏫 Funkcjonowanie w szkole',
    plainHeader: 'Funkcjonowanie w szkole',
    animClass: 'ai-section-4',
    boxClass: SECTION_BOX,
    copyBtnClass: 'hover:bg-purple-50 dark:hover:bg-purple-950/40 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300',
    headerClass: 'text-purple-800 dark:text-purple-300',
    getContent: (i) => i.classroom_implications || i.school_functioning,
  },
  {
    copyTitle: 'Zalecenia dla nauczycieli',
    header: '👨‍🏫 Wskazówki dydaktyczne (dla nauczycieli)',
    plainHeader: 'Wskazówki dydaktyczne (dla nauczycieli)',
    animClass: 'ai-section-5',
    boxClass: SECTION_BOX,
    copyBtnClass: 'hover:bg-indigo-50 dark:hover:bg-indigo-950/40 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300',
    headerClass: 'text-indigo-800 dark:text-indigo-300',
    getContent: (i) => i.teacher_recommendations,
    isList: true,
  },
  {
    copyTitle: 'Zalecenia dla rodziców',
    header: '👨‍👩‍👧 Wsparcie domowe (dla rodziców)',
    plainHeader: 'Wsparcie domowe (dla rodziców)',
    animClass: 'ai-section-6',
    boxClass: SECTION_BOX,
    copyBtnClass: 'hover:bg-pink-50 dark:hover:bg-pink-950/40 border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300',
    headerClass: 'text-pink-800 dark:text-pink-300',
    getContent: (i) => i.parent_recommendations,
    isList: true,
  },
];

const contentToCopyString = (content: ListContent): string => {
  if (Array.isArray(content)) return content.join('\n- ');
  return content || '';
};

const isContentEmpty = (content: ListContent): boolean => {
  if (Array.isArray(content)) return content.filter((item) => item && item.trim() !== '').length === 0;
  return !content || content.trim() === '';
};

/**
 * Składa wszystkie niepuste sekcje interpretacji w czysty tekst:
 * nagłówek sekcji + treść, rozdzielone pustą linią; listy zaleceń jako „- pozycja".
 */
export const buildInterpretationPlainText = (interpretation: Interpretation): string => {
  const parts: string[] = [];
  SECTIONS.forEach((section) => {
    const content = section.getContent(interpretation);
    if (isContentEmpty(content)) return;
    const body = Array.isArray(content)
      ? content.filter((item) => item && item.trim() !== '').map((item) => `- ${item}`).join('\n')
      : (content as string).trim();
    parts.push(`${section.plainHeader}\n${body}`);
  });
  return parts.join('\n\n');
};

interface InterpretationSectionsProps {
  interpretation: Interpretation;
  onCopySection: (title: string, content: string) => void;
  /** compact = panel wyniku (text-xs), reading = pełny ekran (text-sm/base). */
  size?: 'compact' | 'reading';
  /**
   * Kaskadowa animacja wejścia sekcji (klasy ai-section-*). Default true.
   * FullscreenResultDialog wyłącza ją przy ponownym pokazaniu treści (np. po
   * wyjściu z trybu edycji), żeby remount nie odpalał animacji od nowa (M-3).
   */
  animateIn?: boolean;
  className?: string;
}

const InterpretationSectionsComponent: React.FC<InterpretationSectionsProps> = ({
  interpretation,
  onCopySection,
  size = 'compact',
  animateIn = true,
  className,
}) => {
  const [copiedSection, setCopiedSection] = React.useState<string | null>(null);

  const handleCopy = (title: string, content: string) => {
    onCopySection(title, content);
    setCopiedSection(title);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const reading = size === 'reading';
  const headerTextClass = reading ? 'text-sm mb-3' : 'text-xs mb-2';
  const bodyTextClass = reading
    ? 'text-sm md:text-base text-txt-secondary font-medium leading-relaxed pr-6'
    : 'text-xs text-txt-secondary font-medium leading-relaxed pr-6';
  const boxPadding = reading ? 'p-5' : 'p-4';
  const iconSize = reading ? 'w-4 h-4' : 'w-3.5 h-3.5';

  return (
    <div className={cn('flex flex-col', reading ? 'gap-5' : 'gap-4', className)}>
      {SECTIONS.map((section) => {
        const content = section.getContent(interpretation);
        return (
          <div
            key={section.copyTitle}
            className={cn(
              boxPadding,
              'rounded-xl border relative group select-text',
              animateIn && section.animClass,
              section.boxClass
            )}
          >
            <button
              onClick={() => handleCopy(section.copyTitle, contentToCopyString(content))}
              type="button"
              title={`Kopiuj sekcję: ${section.copyTitle}`}
              aria-label={`Kopiuj sekcję: ${section.copyTitle}`}
              className={cn(
                'absolute top-3 right-3 p-1.5 bg-surface-card border rounded-lg transition-all duration-200 shadow-sm',
                section.copyBtnClass
              )}
            >
              {copiedSection === section.copyTitle ? <Check className={iconSize} /> : <Copy className={iconSize} />}
            </button>
            {/* pr-10: miejsce na przycisk kopiowania (absolute) — długie nagłówki nie wchodzą pod niego */}
            <h3 className={cn('font-bold uppercase tracking-wider pr-10', headerTextClass, section.headerClass)}>
              {section.header}
            </h3>
            {section.isList && Array.isArray(content) ? (
              <ul className={cn('list-disc pl-4 flex flex-col gap-1 mt-1', bodyTextClass, 'pr-6')}>
                {content.map((rec, index) => (
                  <li key={index}>{rec}</li>
                ))}
              </ul>
            ) : (
              // whitespace-pre-line: akapity i linie „- …” z odpowiedzi AI zostają w osobnych liniach
              // (wcześniej HTML zlewał je w jeden akapit: „- Bogate słownictwo … - Dobre rozumowanie …”).
              <p className={cn(bodyTextClass, 'whitespace-pre-line')}>{content as string}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * React.memo (C-2): FullscreenResultDialog re-renderuje się przy każdej zmianie
 * lokalnego stanu (klawisze w edytorze, menu eksportu) — propsy sekcji są wtedy
 * referencyjnie stabilne, więc memo ucina zbędne renderowanie 6 sekcji.
 */
export const InterpretationSections = React.memo(InterpretationSectionsComponent);
InterpretationSections.displayName = 'InterpretationSections';
